// components/InvoiceDetail.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Alert,
  Skeleton,
  Divider,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack,
  PictureAsPdf,
  Visibility,
} from '@mui/icons-material';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';

import {
  SETTLEMENT_STATUS_COLORS,
  SETTLEMENT_STATUS_LABELS,
  invoiceStatusKey,
} from '../utils/settlementStatus';

// One page for both invoices of a statement. kind="invoice": HandOff's invoice to the retailer
// (orders it received, 10%). kind="self_bill": the retailer's invoice to HandOff, made by HandOff
// (orders it sent, 8%).
const COPY = {
  invoice: {
    title: 'HandOff invoice',
    load: (id, opts) => dashboardAPI.getInvoiceDetail(id, opts),
    pdf: (id, opts) => dashboardAPI.downloadInvoicePdf(id, opts),
    notPaid: 'You do not pay this invoice on its own. It is settled against your invoice to HandOff for the same period',
    ordersTitle: 'Orders you received',
    amountLabel: 'Commission',
  },
  self_bill: {
    title: 'Your invoice to HandOff',
    load: (id, opts) => dashboardAPI.getSelfBillDetail(id, opts),
    pdf: (id, opts) => dashboardAPI.downloadSelfBillPdf(id, opts),
    notPaid: 'HandOff made this invoice in your name. HandOff does not pay it on its own. It is settled against HandOff\'s invoice to you for the same period',
    ordersTitle: 'Orders you sent',
    amountLabel: 'You earn',
  },
};

const InvoiceDetail = ({ kind = 'invoice' }) => {
  const copy = COPY[kind];
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const testMode = searchParams.get('test_mode') === 'true';

  const [invoice, setInvoice] = useState(null);
  const [commissionItems, setCommissionItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatPeriod = (start, end) => {
    if (!start || !end) return '-';
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await copy.load(id, { testMode });

        if (response.success) {
          setInvoice(response.data.invoice);
          setCommissionItems(response.data.commission_items || []);
        } else {
          setError(response.error || 'Failed to load invoice');
        }
      } catch (err) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoice();
  }, [id, testMode, copy]);

  const handleDownloadPdf = async () => {
    try {
      await copy.pdf(id, { testMode });
    } catch (err) {
      console.error('Failed to download PDF:', err);
    }
  };

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={200} sx={{ mb: 2, borderRadius: 2 }} />
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
          Back to Invoices
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!invoice) return null;

  return (
    <Box>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/invoices')} sx={{ mb: 2 }}>
        Back to Invoices
      </Button>

      {/* Invoice Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Typography variant="h5">
                  {copy.title} {invoice.invoice_number}
                </Typography>
                <Chip
                  label={SETTLEMENT_STATUS_LABELS[invoiceStatusKey(invoice)]}
                  color={SETTLEMENT_STATUS_COLORS[invoiceStatusKey(invoice)]}
                />
              </Box>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Period: {formatPeriod(invoice.supply_period_start, invoice.supply_period_end)}
              </Typography>
              <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid>
                  <Typography variant="caption" color="textSecondary">Issued</Typography>
                  <Typography variant="body2">{formatDate(invoice.issue_date)}</Typography>
                </Grid>
                {invoice.statement_number && (
                  <Grid>
                    <Typography variant="caption" color="textSecondary">Statement</Typography>
                    <Typography variant="body2">{invoice.statement_number}</Typography>
                  </Grid>
                )}
                {invoice.settlement_due_date && (
                  <Grid>
                    <Typography variant="caption" color="textSecondary">Statement due</Typography>
                    <Typography variant="body2">{formatDate(invoice.settlement_due_date)}</Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {invoice.has_pdf && (
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={handleDownloadPdf}
              >
                Download PDF
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Alert severity="warning" sx={{ mb: 2 }}>
            {copy.notPaid}
            {invoice.statement_number ? `. Statement ${invoice.statement_number} shows the balance` : ''}.
            {invoice.rejection_window_ends_at &&
              ` Not right? Reply to the e-mail before ${formatDate(invoice.rejection_window_ends_at)}.`}
          </Alert>

          {/* Financial Summary */}
          <Box sx={{ maxWidth: 350 }}>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">Subtotal (excl. VAT)</Typography>
              <Typography variant="body2">{formatCurrency(invoice.subtotal_excl_vat)}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between" mb={0.5}>
              <Typography variant="body2" color="textSecondary">
                VAT ({invoice.vat_rate}%)
              </Typography>
              <Typography variant="body2">{formatCurrency(invoice.vat_amount)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body1" fontWeight="bold">Total</Typography>
              <Typography variant="body1" fontWeight="bold">{formatCurrency(invoice.total_amount)}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Commission Items */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {copy.ordersTitle} ({commissionItems.length})
          </Typography>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Order Date</TableCell>
                  <TableCell>Order #</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Partner</TableCell>
                  <TableCell align="right">{copy.amountLabel}</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {commissionItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="textSecondary" sx={{ py: 2 }}>
                        No orders linked to this invoice.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  commissionItems.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>{formatDate(item.order_timestamp)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {item.order_number}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Box>
                          <Typography variant="body2" noWrap title={item.product_name}>
                            {item.product_name}
                          </Typography>
                          {(item.product_color || item.product_size) && (
                            <Typography variant="caption" color="textSecondary">
                              {[item.product_color, item.product_size].filter(Boolean).join(' / ')}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{item.partner_name}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(item.commission_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Commission Details">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/commissions/${item.id}`, { state: { fromInvoice: id, fromInvoiceKind: kind, testMode } })}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default InvoiceDetail;
